(() => {
  if (customElements.get('vx-pack-selector')) return;
  const sources = new Map();
  const discountPlans = new Map();
  const loadPlans = path => {
    if (!discountPlans.has(path)) discountPlans.set(path, fetch(path).then(response => {
      if (!response.ok) throw new Error('Discounts unavailable');
      return response.json();
    }).catch(error => { discountPlans.delete(path); throw error; }));
    return discountPlans.get(path);
  };
  const packPlan = (plans, key, count, subtotal) => count === 1
    ? {total: subtotal, code: ''} : plans[key]?.tiers[count]?.[subtotal];
  const coverSaving = quantity => quantity === 2 ? 1000 : quantity === 3 ? 3000 : 0;
  const sizeOf = (variant, product) => {
    const match = variant.title.match(/(?:^|\s|\/)\b(\d+(?:[.,]\d+)?)\s*ml\b/i)
      || product.title.match(/\b(\d+(?:[.,]\d+)?)\s*ml\b/i)
      || (product.tags || []).join(' ').match(/\b(\d+(?:[.,]\d+)?)\s*ml\b/i);
    return match ? match[1].replace(',', '.') + ' ml' : '';
  };
  const seriesOf = product => (product.tags || []).includes('VORXAZ Unlimited') ? 'vxu'
    : (product.tags || []).includes('VORXAZ') ? 'vx'
    : product.vendor.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const eligible = product => {
    const tags = (product.tags || []).join('|').toLowerCase();
    const title = product.title.toLowerCase();
    return (seriesOf(product) === 'kosy-mosy' || !/hel parfumeflaske|hel flaske/.test(tags))
      && !/gavesæt|gaver & sæt|b2b 4-pak|discovery|prøvesæt|gavepakke|duftsæt/.test(tags)
      && !/gavesæt|discovery|4-pak/.test(title);
  };
  const flatten = products => products.filter(eligible).flatMap(product => {
    const hasRefill = product.variants.some(variant => variant.title === 'Ingen cover');
    return product.variants.filter(variant => !hasRefill || variant.title === 'Ingen cover')
      .map(variant => ({ ...variant, name: product.title, productId: product.id, series: seriesOf(product), size: sizeOf(variant, product) }));
  });
  const loadSource = (path) => {
    if (!sources.has(path)) sources.set(path, (async () => {
      const products = [];
      let next = path;
      const visited = new Set();
      while (next) {
        const url = new URL(next, location.origin);
        url.searchParams.set('view', 'vx-pack-data');
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        if (visited.has(url.href)) throw new Error('Pagination loop');
        visited.add(url.href);
        const response = await fetch(url, { credentials: 'same-origin' });
        if (!response.ok) throw new Error('Collection unavailable');
        const data = await response.json();
        products.push(...data.products);
        next = data.next;
      }
      return flatten(products);
    })().catch(error => { sources.delete(path); throw error; }));
    return sources.get(path);
  };
  class VxPackSelector extends HTMLElement {
    async connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.text = JSON.parse(this.querySelector('.vx-pack-text').textContent);
      this.form = this.querySelector('form');
      this.count = this.querySelector('.vx-pack-count');
      this.fields = this.querySelector('.vx-pack-scents');
      this.total = this.querySelector('.vx-pack-total');
      this.error = this.querySelector('.vx-pack-error');
      this.button = this.querySelector('.vx-pack-submit');
      this.money = cents => new Intl.NumberFormat(document.documentElement.lang || 'da-DK', {style: 'currency', currency: this.dataset.currency || 'DKK'}).format(cents / 100);
      this.product = JSON.parse(this.querySelector('.vx-pack-current').textContent);
      this.covers = JSON.parse(this.querySelector('.vx-pack-covers').textContent) || [];
      this.productInfo = this.closest('product-info');
      this.count.addEventListener('change', () => this.render());
      this.fields.addEventListener('change', () => this.refresh());
      this.form.addEventListener('submit', event => this.submit(event));
      this.onCoverChange = event => { if (event.target.closest('cover-selector')) this.refresh(); };
      this.productInfo.addEventListener('change', this.onCoverChange);
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({data}) => {
        if (data.sectionId === this.productInfo.dataset.section) this.setVariant(data.variant);
      });
      try {
        [this.allScents, this.plans] = await Promise.all([
          loadSource(this.dataset.source),
          loadPlans(JSON.parse(this.querySelector('.vx-pack-discounts-url').textContent))
        ]);
        const current = flatten([this.product]);
        this.allScents = this.allScents.filter(scent => String(scent.productId) !== String(this.product.id)).concat(current);
        const id = this.productInfo.querySelector('product-form input[name="id"]')?.value || this.dataset.current;
        this.setVariant(this.product.variants.find(variant => String(variant.id) === String(id)));

      } catch (error) { this.showError(this.text.load_error); }
    }
    disconnectedCallback() {
      this.unsubscribe?.();
      this.productInfo?.removeEventListener('change', this.onCoverChange);
    }
    setVariant(variant) {
      if (!this.allScents) return;
      const size = variant ? sizeOf(variant, this.product) : '';
      this.hidden = !size;
      if (!size) { this.button.disabled = true; return; }
      const changed = size !== this.size;
      this.size = size;
      this.scents = this.allScents.filter(scent => scent.series === this.dataset.series && scent.size === size);
      this.planKey = `${this.dataset.series}:${size}`;
      this.discounted = Boolean(this.plans[this.planKey]);
      const current = this.scents.find(scent => String(scent.productId) === String(this.product.id));
      this.dataset.current = current ? String(current.id) : '';
      this.querySelector('summary').textContent = this.discounted ? this.text.heading : this.text.heading_plain;
      this.querySelector('label').textContent = `${this.text.size} — ${size}`;
      this.render(changed);
    }
    cover() {
      const selector = this.productInfo.querySelector('cover-selector');
      const id = selector?.selectedCoverVariantId;
      return this.size === '10 ml' && id ? this.covers.find(cover => String(cover.id) === String(id)) : null;
    }
    selected() {
      return [...this.fields.querySelectorAll('select')].map(select => this.scents.find(scent => String(scent.id) === select.value));
    }
    showError(message) { this.error.textContent = message; this.error.hidden = false; }
    render(sizeChanged = false) {
      const previous = [...this.fields.querySelectorAll('select')].map(select => {
        if (!sizeChanged) return select.value;
        const old = this.allScents.find(scent => String(scent.id) === select.value);
        return String(this.scents.find(scent => String(scent.productId) === String(old?.productId))?.id || '');
      });
      this.fields.replaceChildren();
      for (let index = 0; index < Number(this.count.value); index++) {
        const label = document.createElement('label');
        const select = document.createElement('select');
        select.id = `${this.querySelector('.vx-pack-count').id}-scent-${index}`;
        select.required = true;
        label.htmlFor = select.id;
        label.textContent = `${this.text.scent} ${index + 1}`;
        select.add(new Option(this.text.choose, ''));
        for (const scent of this.scents) {
          const option = new Option(scent.name + (scent.title === 'Ingen cover' ? '' : ` — ${scent.title}`) + (scent.available ? '' : ` — ${this.text.sold_out}`), String(scent.id));
          option.disabled = !scent.available;
          select.add(option);
        }
        select.value = previous[index] || (index === 0 ? this.dataset.current : '');
        if (select.selectedOptions[0]?.disabled) select.value = '';
        this.fields.append(label, select);
      }
      this.refresh();
    }
    refresh() {
      const count = Number(this.count.value);
      const selected = [...this.fields.querySelectorAll('select')];
      const chosen = this.selected();
      const base = chosen[0]?.price || this.scents.find(scent => String(scent.id) === this.dataset.current)?.price || Math.min(...this.scents.filter(scent => scent.available).map(scent => scent.price));
      const cover = this.cover();
      const extra = Number(cover?.price || 0);
      for (const option of this.count.options) {
        const quantity = Number(option.value);
        const plan = packPlan(this.plans, this.planKey, quantity, base * quantity);
        const price = plan?.total;
        const coverTotal = quantity * extra - (cover ? coverSaving(quantity) : 0);
        option.textContent = `${quantity} × ${this.size}${Number.isFinite(price) ? ' — ' + this.money(price + coverTotal) : ''}${this.size === '100 ml' && ['vx', 'vxu'].includes(this.dataset.series) && quantity === 3 ? ' · ' + this.text.free : ''}`;
      }
      const complete = chosen.length === count && chosen.every(Boolean);
      const subtotal = chosen.reduce((sum, scent) => sum + Number(scent?.price || 0), 0);
      const plan = packPlan(this.plans, this.planKey, count, subtotal);
      this.total.textContent = complete && plan ? `${this.text.total}: ${this.money(plan.total + count * extra - (cover ? coverSaving(count) : 0))}` : '';
      this.button.disabled = !complete || !plan || chosen.some(scent => !scent.available) || this.busy || (cover && !cover.available);

    }
    async json(path, body) {
      const response = await fetch(this.dataset.root + path, {credentials: 'same-origin', headers: body ? {'Content-Type': 'application/json'} : {}, ...(body ? {method: 'POST', body: JSON.stringify(body)} : {})});
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || this.text.error);
      return data;
    }
    async submit(event) {
      event.preventDefault();
      if (!this.form.reportValidity()) return;
      this.busy = true;
      this.button.disabled = true;
      this.error.hidden = true;
      const count = Number(this.count.value);
      const group = crypto.randomUUID();
      const scents = this.scents;
      const planKey = this.planKey;
      const size = this.size;
      const plans = this.plans;
      const chosen = this.selected();
      const chosenCover = this.cover();
      const coverIds = new Set(this.covers.map(cover => String(cover.id)));
      let originalCodes = [];
      let added = false;
      try {
        const before = await this.json('cart.js');
        originalCodes = (before.discount_codes || []).map(code => code.code);
        const ids = new Set(scents.map(scent => String(scent.id)));
        const existingQuantity = before.items.filter(item => ids.has(String(item.variant_id))).reduce((sum, item) => sum + item.quantity, 0);
        if (existingQuantity + count > 3) throw new Error(this.text.limit_error);
        const items = chosen.map(scent => ({id: scent.id, quantity: 1, properties: {
          _vx_pack_series: this.dataset.series, _vx_pack_size: size, _vx_pack_group: group
        }}));
        if (chosenCover) items.push({id: chosenCover.id, quantity: count, properties: {
          _vx_pack_series: this.dataset.series, _vx_pack_size: size, _vx_pack_cover: 'true', _vx_pack_group: group
        }});
        await this.json('cart/add.js', {items});
        added = true;
        const cart = await this.json('cart.js');
        const relevant = cart.items.filter(item => ids.has(String(item.variant_id)));
        const quantity = relevant.reduce((sum, item) => sum + item.quantity, 0);
        const subtotal = relevant.reduce((sum, item) => sum + item.original_line_price, 0);
        const plan = packPlan(plans, planKey, quantity, subtotal);
        if (!plan) throw new Error(this.text.price_error);
        const tierCode = Object.values(plans[planKey]?.tiers?.[2] || {})[0]?.code;
        const groupPrefix = tierCode ? tierCode.split('-2-')[0] + '-' : null;
        const legacyPrefix = planKey === 'vx:100 ml' ? 'VX100-' : planKey === 'vxu:100 ml' ? 'VXU100-' : null;
        const codes = originalCodes.filter(code => !(groupPrefix && code.toUpperCase().startsWith(groupPrefix))
          && !code.toUpperCase().startsWith('DC-PK-COVER-')
          && !(legacyPrefix && code.toUpperCase().startsWith(legacyPrefix)));
        if (plan.code) codes.push(plan.code);
        const coverGroups = new Map();
        const coverLines = cart.items.filter(item => coverIds.has(String(item.variant_id)));
        for (const item of coverLines) {
          if (item.properties?._vx_pack_cover !== 'true') continue;
          const key = `${item.properties._vx_pack_series}:${item.properties._vx_pack_size}`;
          coverGroups.set(key, (coverGroups.get(key) || 0) + item.quantity);
        }
        const saving = [...coverGroups.values()].reduce((sum, qty) => sum + coverSaving(qty), 0);
        if (saving > 15000) throw new Error(this.text.price_error);
        if (saving) codes.push(`DC-PK-COVER-${saving}`);
        const updated = await this.json('cart/update.js', {discount: codes.join(',')});
        for (const code of [plan.code, saving ? `DC-PK-COVER-${saving}` : ''].filter(Boolean)) {
          if (!(updated.discount_codes || []).some(discount => discount.code.toUpperCase() === code.toUpperCase() && discount.applicable)) throw new Error(this.text.price_error);
        }
        const actual = updated.items.filter(item => ids.has(String(item.variant_id))).reduce((sum, item) => sum + item.final_line_price, 0);
        const coverActual = updated.items.filter(item => coverIds.has(String(item.variant_id))).reduce((sum, item) => sum + item.final_line_price, 0);
        const coverExpected = coverLines.reduce((sum, item) => sum + item.original_line_price, 0) - saving;
        if (actual !== plan.total || coverActual !== coverExpected) throw new Error(this.text.price_error);
        location.assign(this.dataset.root + 'cart');
      } catch (error) {
        if (added) {
          try {
            const cart = await this.json('cart.js');
            const updates = Object.fromEntries(cart.items.filter(item => item.properties?._vx_pack_group === group).map(item => [item.key, 0]));
            await this.json('cart/update.js', {updates, discount: originalCodes.join(',')});
          } catch (rollbackError) { this.showError(this.text.error); return; }
        }
        this.showError(error.message || this.text.error);
      } finally { this.busy = false; this.refresh(); }
    }
  }
  customElements.define('vx-pack-selector', VxPackSelector);
})();

